// src/PreloginPage.tsx
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Button, Alert, Spinner, Card } from "react-bootstrap";
import { configService } from "./Services/Config/ConfigService";
import { AuthContext } from "./Auth/AuthProvider";

type CheckReq = { emailAddress: string };

// Match the controller contract
type CheckRespOk = {
    exists: true;
    firstTime?: boolean;
    message?: string;
    nativeSignInUrl?: string; // may exist but we won't require it for firstTime messaging
};

type CheckRespErr = { exists: false; message?: string };

function isOk(x: any): x is CheckRespOk {
    return x && x.exists === true;
}

const REDIRECT_URI_KEY = "redirectUri";

const PreloginPage: React.FC = () => {
    const auth = useContext(AuthContext);
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // NEW: first-time flow state (message only; no nativeSignInUrl required)
    const [firstTimeMode, setFirstTimeMode] = useState(false);

    // If we land on /prelogin while already authenticated,
    // immediately send the user to the stored redirectUri (or "/").
    useEffect(() => {
        (async () => {
            await auth.onReady?.();
            const isAuth = auth.isAuthenticated?.() ?? false;
            console.log("[PreloginPage] mount, isAuthenticated:", isAuth);

            if (isAuth) {
                let redirect = "/";
                try {
                    const stored = localStorage.getItem(REDIRECT_URI_KEY) || "";
                    if (stored) {
                        redirect = stored;
                        localStorage.removeItem(REDIRECT_URI_KEY);
                    }
                } catch {
                    /* ignore and fall back to "/" */
                }

                console.log("[PreloginPage] already authenticated; redirecting to", redirect);
                navigate(redirect || "/", { replace: true });
            }
        })();
    }, [auth, navigate]);

    // Shared “start sign-in” action (used by Continue and Next)
    const startLoginRedirect = async () => {
        // Retrieve redirectUri stored in the gate (default to "/")
        let redirectUri = "/";
        try {
            redirectUri = localStorage.getItem(REDIRECT_URI_KEY) || redirectUri;
        } catch {
            /* ignore; keep "/" */
        }

        console.log("[PreloginPage] calling loginRedirect with state", redirectUri);

        await auth.loginRedirect?.({
            scopes: [], // AuthService will merge in defaults
            loginHint: email.trim(),
            state: redirectUri, // e.g. "/onbehalfof/4175?oboToken=..."
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        setFirstTimeMode(false); // reset on each submit

        try {
            const normalizedEmail = email.trim();
            if (!normalizedEmail) {
                setError("Email address is required.");
                setLoading(false);
                return;
            }

            const cfg = await configService.getConfiguration();
            const baseUri = (cfg.PreCertApiUrl ?? "").replace(/\/+$/, "");

            const payload: CheckReq = { emailAddress: normalizedEmail };

            const res = await fetch(`${baseUri}/api/PreLoginPrecertUser/CheckAndSavePreCertUser`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            });

            console.log("[PreloginPage] status", res.status, res.statusText);
            const text = await res.text();
            console.log("[PreloginPage] raw response", text);
            let body: CheckRespOk | CheckRespErr | null = null;

            if (text) {
                try {
                    body = JSON.parse(text);
                } catch {
                    // ignore JSON parse error; treat as generic failure
                }
            }

            if (!res.ok) {
                const serverMessage = (body as any)?.message || text || `Request failed with status ${res.status}`;
                throw new Error(serverMessage);
            }

            if (!body) {
                throw new Error("Server returned an empty or invalid response.");
            }

            if (!isOk(body)) {
                setError(body.message || "This email is not recognized.");
                setLoading(false);
                return;
            }

            // ✅ Email exists / created in DB+Entra.
            // If first-time, show message and require user to click Next (no nativeSignInUrl needed)
            if (body.firstTime) {
                setFirstTimeMode(true);
                setLoading(false);
                return;
            }

            // Existing user -> proceed to login redirect immediately
            await startLoginRedirect();
        } catch (err: any) {
            console.error("[PreloginPage] error", err);
            setError(err?.message || "Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col md={6} lg={5}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title as="h2" className="h4 mb-3">
                                PreCert Sign In
                            </Card.Title>
                            <Form onSubmit={handleSubmit} noValidate>
                                <Form.Group controlId="email">
                                    <Form.Label>Email address</Form.Label>
                                    <Form.Control
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={loading}
                                        autoComplete="email"
                                    />
                                </Form.Group>
                                {error && <Alert variant="danger">{error}</Alert>}

                                {/* Info message for first-time users created in Entra (NO nativeSignInUrl dependency) */}
                                {firstTimeMode && (
                                    <Alert className="mb-3" style={{ backgroundColor: '#fffacd', borderColor: '#ffd700', color: '#333' }}>
                                        <p>
                                            Your account has been set up. You will receive an email with a temporary password at the email address you provided. Once you receive it, click <strong>Next</strong> and follow the on-screen instructions to complete the setup process.
                                        </p>
                                        <p>
                                            At a later step, you will be asked to create a new password. The <strong>Password Requirements</strong> are as follows:
                                        </p>
                                        <ul className="mb-0">
                                            <li>8 to 256 characters</li>
                                            <li>Use at least 3 of the following:
                                                <ul>
                                                    <li>Uppercase letters (A-Z)</li>
                                                    <li>Lowercase letters (a-z)</li>
                                                    <li>Numbers (0-9)</li>
                                                    <li>Special characters</li>
                                                </ul>
                                            </li>
                                            <li>Avoid common or easily guessed passwords</li>
                                            <li>Your password cannot be the same as your current password</li>
                                        </ul>
                                    </Alert>
                                )}
                                <div className="mt-3 d-flex align-items-center gap-2">
                                    {!firstTimeMode && (
                                        <Button
                                            type="submit"
                                            disabled={loading || !email.trim()}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Checking...
                                                </>
                                            ) : (
                                                "Continue"
                                            )}
                                        </Button>
                                    )}

                                    {/* Next button shown only in first-time mode */}
                                    {firstTimeMode && (
                                        <Button
                                            type="button"
                                            variant="outline-primary"
                                            disabled={loading || !email.trim()}
                                            onClick={async () => {
                                                try {
                                                    setError(null);
                                                    setLoading(true);
                                                    await startLoginRedirect();
                                                } catch (e: any) {
                                                    console.error("[PreloginPage] Next/loginRedirect failed", e);
                                                    setError(e?.message || "Unable to start sign-in. Please try again.");
                                                    setLoading(false);
                                                }
                                            }}
                                        >
                                            Next
                                        </Button>
                                    )}
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                </Col>
            </Row>
        </Container>
    );
};

export default PreloginPage;
